import { useState, useEffect, useCallback, useRef } from 'react'
import { Truck, Plus, Pencil, Trash2, AlertTriangle, ChevronDown, X, Check, RefreshCw, UserPlus, Search } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import portariaService from '../../services/portariaService'
import leiloesService from '../../services/leiloesService'
import clientesService from '../../services/clientesService'
import NovoClienteModal from '../../components/modals/NovoClienteModal'
import toast from 'react-hot-toast'

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDT(dt) {
  if (!dt) return '—'
  return new Date(dt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function nowLocalISO() {
  const now = new Date()
  const pad = n => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
}

// ─── Saldo Badge ─────────────────────────────────────────────────────────────
function SaldoBadge({ saldo }) {
  if (!saldo) return null
  const { total_chegado, total_em_lotes, saldo: diff, total_machucado } = saldo
  const cor = diff < 0
    ? 'border-red-300 bg-red-50'
    : diff === 0
    ? 'border-green-300 bg-green-50'
    : 'border-blue-300 bg-blue-50'
  return (
    <div className={`flex flex-wrap gap-4 rounded-xl border px-5 py-3 text-sm ${cor}`}>
      <span><span className="font-semibold text-gray-700">Chegaram:</span> <strong>{total_chegado}</strong> cab</span>
      <span><span className="font-semibold text-gray-700">Em lotes:</span> <strong>{total_em_lotes}</strong> cab</span>
      <span>
        <span className="font-semibold text-gray-700">Saldo:</span>{' '}
        <strong className={diff < 0 ? 'text-red-600' : 'text-green-700'}>
          {diff >= 0 ? '+' : ''}{diff}
        </strong> cab
      </span>
      {total_machucado > 0 && (
        <span className="flex items-center gap-1 text-amber-700">
          <AlertTriangle size={13} /> <strong>{total_machucado}</strong> machucado(s)
        </span>
      )}
    </div>
  )
}

// ─── Modal de Formulário ─────────────────────────────────────────────────────
function ModalEntrada({ leilaoId, orgId, initial, onSaved, onClose }) {
  const emptyForm = {
    leilao_id:        leilaoId,
    vendedor_id:      null,
    vendedor_nome:    '',
    fazenda:          '',
    fazenda_id:       null,
    qtd_chegada:      '',
    qtd_machucado:    0,
    obs_machucado:    '',
    motorista:        '',
    placa:            '',
    obs:              '',
    data_hora_entrada: nowLocalISO(),
  }

  const [form, setForm]               = useState(initial?.id
    ? { ...emptyForm, ...initial, fazenda: initial.fazenda || '', data_hora_entrada: initial.data_hora_entrada ? initial.data_hora_entrada.slice(0,16) : nowLocalISO() }
    : emptyForm
  )
  const [vendSearch, setVendSearch]   = useState(initial?.vendedor?.nome || initial?.vendedor_nome || '')
  const [vendOpcoes, setVendOpcoes]   = useState([])
  const [vendDropOpen, setVendDropOpen] = useState(false)
  const [fazendas, setFazendas]       = useState([])
  const [saving, setSaving]           = useState(false)
  const [showNovoCliente, setShowNovoCliente] = useState(false)
  const searchRef                     = useRef(null)
  const dropRef                       = useRef(null)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // Busca em tempo real de clientes
  useEffect(() => {
    if (vendSearch.length < 2) { setVendOpcoes([]); setVendDropOpen(false); return }
    const t = setTimeout(async () => {
      try {
        const data = await clientesService.listarClientes(orgId, 1, 30, vendSearch)
        setVendOpcoes(data.data || [])
        setVendDropOpen(true)
      } catch { setVendOpcoes([]) }
    }, 250)
    return () => clearTimeout(t)
  }, [vendSearch, orgId])

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setVendDropOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Carregar fazendas quando vendedor_id mudar
  useEffect(() => {
    if (!form.vendedor_id) { setFazendas([]); return }
    clientesService.getById(orgId, form.vendedor_id)
      .then(c => {
        const faz = c.fazendas || []
        setFazendas(faz)
        if (faz.length === 1 && !form.fazenda_id) {
          set('fazenda_id', faz[0].id)
          set('fazenda', faz[0].nome)
        }
      })
      .catch(() => setFazendas([]))
  }, [form.vendedor_id])

  const selecionarVendedor = (c) => {
    set('vendedor_id', c.id)
    set('vendedor_nome', c.nome)
    setVendSearch(c.nome)
    setVendDropOpen(false)
    setVendOpcoes([])
  }

  const limparVendedor = () => {
    set('vendedor_id', null)
    set('vendedor_nome', '')
    set('fazenda_id', null)
    set('fazenda', '')
    setVendSearch('')
    setFazendas([])
    setTimeout(() => searchRef.current?.focus(), 50)
  }

  const handleClienteCriado = (c) => {
    selecionarVendedor(c)
    if (c.fazendas?.length) {
      setFazendas(c.fazendas)
      set('fazenda_id', c.fazendas[0].id)
      set('fazenda', c.fazendas[0].nome)
    }
    setShowNovoCliente(false)
  }

  const handleFazendaChange = (fazendaId) => {
    const f = fazendas.find(x => String(x.id) === String(fazendaId))
    set('fazenda_id', fazendaId ? Number(fazendaId) : null)
    set('fazenda', f ? f.nome : '')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.qtd_chegada || parseInt(form.qtd_chegada) <= 0) {
      toast.error('Informe a quantidade de chegada')
      return
    }
    setSaving(true)
    try {
      const payload = {
        leilao_id:        parseInt(leilaoId),
        vendedor_id:      form.vendedor_id || null,
        vendedor_nome:    form.vendedor_id ? null : (form.vendedor_nome || null),
        fazenda:          form.fazenda || null,
        qtd_chegada:      parseInt(form.qtd_chegada),
        qtd_machucado:    parseInt(form.qtd_machucado) || 0,
        obs_machucado:    form.obs_machucado || null,
        motorista:        form.motorista || null,
        placa:            form.placa || null,
        obs:              form.obs || null,
        data_hora_entrada: form.data_hora_entrada || null,
      }
      if (initial?.id) {
        await portariaService.atualizarEntrada(orgId, initial.id, payload)
        toast.success('Entrada atualizada')
      } else {
        await portariaService.criarEntrada(orgId, payload)
        toast.success('Entrada registrada')
      }
      onSaved()
    } catch (err) {
      const detail = err.response?.data?.detail
      toast.error(Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : (detail || 'Erro ao salvar'))
    } finally {
      setSaving(false)
    }
  }

  const labelCls = 'block text-xs font-medium text-gray-600 mb-1'
  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-green-100 rounded-lg">
                <Truck size={18} className="text-green-700" />
              </div>
              <h2 className="text-base font-semibold text-gray-800">
                {initial?.id ? 'Editar entrada' : 'Nova entrada'}
              </h2>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
              <X size={18} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

            {/* Vendedor */}
            <div ref={dropRef} className="relative">
              <div className="flex items-center justify-between mb-1">
                <label className={labelCls + ' mb-0'}>Vendedor</label>
                {!form.vendedor_id && (
                  <button type="button" onClick={() => setShowNovoCliente(true)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                    <UserPlus size={11} /> Cadastrar novo
                  </button>
                )}
              </div>
              {form.vendedor_id ? (
                <div className="flex items-center gap-2 border border-green-300 bg-green-50 rounded-lg px-3 py-2">
                  <Check size={14} className="text-green-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-green-800 flex-1">{vendSearch}</span>
                  <button type="button" onClick={limparVendedor}
                    className="text-green-500 hover:text-red-500 transition-colors">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    ref={searchRef}
                    className={inputCls + ' pl-8'}
                    placeholder="Buscar por nome..."
                    value={vendSearch}
                    onChange={e => { setVendSearch(e.target.value); set('vendedor_nome', e.target.value) }}
                    autoComplete="off"
                    autoFocus
                  />
                </div>
              )}

              {vendDropOpen && vendOpcoes.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-52 overflow-y-auto">
                  {vendOpcoes.map(c => (
                    <button key={c.id} type="button" onMouseDown={() => selecionarVendedor(c)}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors">
                      <div className="font-medium text-gray-900">{c.nome}</div>
                      {c.fazendas?.length > 0 && (
                        <div className="text-xs text-gray-500 mt-0.5">{c.fazendas.map(f => f.nome).join(', ')}</div>
                      )}
                    </button>
                  ))}
                  <button type="button" onMouseDown={() => { setShowNovoCliente(true); setVendDropOpen(false) }}
                    className="w-full text-left px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2 font-medium border-t border-blue-100">
                    <UserPlus size={13} /> Cadastrar "{vendSearch}"
                  </button>
                </div>
              )}
            </div>

            {/* Fazenda */}
            <div>
              <label className={labelCls}>Fazenda de origem</label>
              {form.vendedor_id && fazendas.length > 0 ? (
                <select
                  value={form.fazenda_id || ''}
                  onChange={e => handleFazendaChange(e.target.value)}
                  className={inputCls}>
                  <option value="">Selecione a fazenda...</option>
                  {fazendas.map(f => (
                    <option key={f.id} value={f.id}>{f.nome}{f.cidade ? ` — ${f.cidade}` : ''}</option>
                  ))}
                </select>
              ) : (
                <input
                  className={inputCls}
                  value={form.fazenda}
                  onChange={e => set('fazenda', e.target.value)}
                  placeholder="Nome da fazenda de origem"
                />
              )}
            </div>

            {/* Qtd chegada + machucado */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Qtd chegada <span className="text-red-500">*</span></label>
                <input type="number" min="1" className={inputCls} value={form.qtd_chegada}
                  onChange={e => set('qtd_chegada', e.target.value)} placeholder="0" required />
              </div>
              <div>
                <label className={labelCls}>Qtd machucado</label>
                <input type="number" min="0" className={inputCls} value={form.qtd_machucado}
                  onChange={e => set('qtd_machucado', e.target.value)} placeholder="0" />
              </div>
            </div>

            {/* Obs machucado */}
            {parseInt(form.qtd_machucado) > 0 && (
              <div>
                <label className={labelCls + ' text-amber-700'}>
                  <AlertTriangle size={12} className="inline mr-1" />Obs. machucado
                </label>
                <textarea rows={2} className={inputCls + ' resize-none'} value={form.obs_machucado}
                  onChange={e => set('obs_machucado', e.target.value)}
                  placeholder="Descreva os animais machucados..." />
              </div>
            )}

            {/* Motorista + Placa */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Motorista</label>
                <input className={inputCls} value={form.motorista}
                  onChange={e => set('motorista', e.target.value)} placeholder="Nome do motorista" />
              </div>
              <div>
                <label className={labelCls}>Placa</label>
                <input className={inputCls} value={form.placa}
                  onChange={e => set('placa', e.target.value.toUpperCase())} placeholder="ABC-1234" />
              </div>
            </div>

            {/* Data/hora */}
            <div>
              <label className={labelCls}>Data/hora de entrada</label>
              <input type="datetime-local" className={inputCls} value={form.data_hora_entrada}
                onChange={e => set('data_hora_entrada', e.target.value)} />
            </div>

            {/* Obs geral */}
            <div>
              <label className={labelCls}>Observações</label>
              <textarea rows={2} className={inputCls + ' resize-none'} value={form.obs}
                onChange={e => set('obs', e.target.value)} placeholder="Observações gerais..." />
            </div>
          </form>

          {/* Footer */}
          <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium">
              Cancelar
            </button>
            <button onClick={handleSubmit} disabled={saving}
              className="flex-1 px-4 py-2.5 text-sm bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-xl font-medium transition-colors">
              {saving ? 'Salvando...' : initial?.id ? 'Atualizar' : 'Registrar entrada'}
            </button>
          </div>
        </div>
      </div>

      {showNovoCliente && (
        <NovoClienteModal
          isOpen
          onClose={() => setShowNovoCliente(false)}
          onClienteCreated={handleClienteCriado}
          organizadorId={orgId}
          defaultGrupo="vendedor"
          title="Novo Vendedor"
        />
      )}
    </>
  )
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function EntradaAnimais() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes,   setLeiloes]   = useState([])
  const [leilaoId,  setLeilaoId]  = useState('')
  const [entradas,  setEntradas]  = useState([])
  const [saldo,     setSaldo]     = useState(null)
  const [loading,   setLoading]   = useState(false)
  const [loadingL,  setLoadingL]  = useState(true)
  const [modal,     setModal]     = useState(null) // null | 'novo' | entrada-object

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .catch(console.error)
      .finally(() => setLoadingL(false))
  }, [orgId])

  const carregar = useCallback(async () => {
    if (!orgId || !leilaoId) return
    setLoading(true)
    try {
      const [ents, sal] = await Promise.all([
        portariaService.listarEntradas(orgId, leilaoId),
        portariaService.saldoAnimais(orgId, leilaoId),
      ])
      setEntradas(ents || [])
      setSaldo(sal)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [orgId, leilaoId])

  useEffect(() => { carregar() }, [carregar])

  const handleSaved = () => {
    setModal(null)
    carregar()
  }

  const handleExcluir = async (id) => {
    if (!window.confirm('Excluir esta entrada?')) return
    try {
      await portariaService.excluirEntrada(orgId, id)
      toast.success('Entrada excluída')
      carregar()
    } catch { toast.error('Erro ao excluir') }
  }

  return (
    <div className="space-y-4">

      {/* Título */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-green-600" />
          <h1 className="text-lg font-semibold text-gray-800">Entrada de Animais</h1>
        </div>
        <button onClick={() => setModal('novo')}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium shadow-sm">
          <Plus size={15} /> Nova entrada
        </button>
      </div>

      {/* Seletor de leilão */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
            <label className="text-xs text-gray-500 font-medium">Leilão</label>
            <div className="relative">
              <select value={leilaoId} onChange={e => setLeilaoId(e.target.value)} disabled={loadingL}
                className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100">
                {loadingL
                  ? <option>Carregando...</option>
                  : leiloes.map(l => (
                      <option key={l.id} value={String(l.id)}>
                        {l.nome} — {new Date(l.data_leilao).toLocaleDateString('pt-BR')}
                      </option>
                    ))
                }
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <button onClick={carregar} disabled={loading}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors self-end">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Saldo */}
      {saldo && <SaldoBadge saldo={saldo} />}

      {/* Lista */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : entradas.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Truck size={36} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Nenhuma entrada registrada.</p>
          <button onClick={() => setModal('novo')}
            className="mt-3 text-sm text-green-600 hover:text-green-800 font-medium">
            + Registrar primeira entrada
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-gray-100">
            {entradas.map(e => (
              <div key={e.id} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">
                      {e.vendedor?.nome || e.vendedor_nome || <span className="text-gray-400 italic">Sem vendedor</span>}
                    </div>
                    {e.fazenda && <div className="text-xs text-gray-500 truncate">{e.fazenda}</div>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setModal(e)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => handleExcluir(e.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="font-bold text-gray-900">{e.qtd_chegada} cab</span>
                  {e.qtd_machucado > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      <AlertTriangle size={10} /> {e.qtd_machucado} mach.
                    </span>
                  )}
                  {(e.motorista || e.placa) && (
                    <span className="text-xs text-gray-500">
                      {[e.motorista, e.placa].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  <span className="text-xs text-gray-400 ml-auto">{fmtDT(e.data_hora_entrada)}</span>
                </div>
              </div>
            ))}
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex justify-between text-xs">
              <span className="text-gray-500 font-medium">{entradas.length} registro(s)</span>
              <span className="font-bold text-gray-800">{entradas.reduce((s, e) => s + (e.qtd_chegada || 0), 0)} cab total</span>
            </div>
          </div>
          {/* Desktop table */}
          <table className="hidden sm:table w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase">Vendedor / Fazenda</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600 text-xs uppercase">Chegada</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-600 text-xs uppercase">Machucado</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase">Motorista / Placa</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 text-xs uppercase">Entrada</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entradas.map(e => (
                <tr key={e.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{e.vendedor?.nome || e.vendedor_nome || <span className="text-gray-400 italic">Sem vendedor</span>}</div>
                    {e.fazenda && <div className="text-xs text-gray-500">{e.fazenda}</div>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-bold text-gray-900">{e.qtd_chegada}</span>
                    <span className="text-xs text-gray-500 ml-1">cab</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {e.qtd_machucado > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <AlertTriangle size={10} /> {e.qtd_machucado}
                      </span>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-gray-800">{e.motorista || <span className="text-gray-400">—</span>}</div>
                    {e.placa && <div className="text-xs text-gray-500 font-mono">{e.placa}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{fmtDT(e.data_hora_entrada)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setModal(e)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => handleExcluir(e.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200">
                <td className="px-4 py-2 text-xs text-gray-500 font-medium">{entradas.length} registro(s)</td>
                <td className="px-4 py-2 text-center font-bold text-gray-800 text-sm">
                  {entradas.reduce((s, e) => s + (e.qtd_chegada || 0), 0)} cab
                </td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <ModalEntrada
          leilaoId={leilaoId}
          orgId={orgId}
          initial={modal === 'novo' ? null : modal}
          onSaved={handleSaved}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  )
}
